import React, { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import appointmentService from '../../../services/appointment.service';
import { showError, showInfo, showSuccess } from '../../../utils/ecxSwal';
import {
    getAppointmentStatusMeta,
    getPatientInitials,
} from './subparts/appointmentsQuery';
import AppointmentReminderPills from './AppointmentReminderPills';

function AppointmentListItem({ appt, isSelected, onCheckChange, onStatusUpdated }) {
    const [updating, setUpdating] = useState(false);

    const patient = appt.patient || {};
    const doctor = appt.doctor || {};
    const statusMeta = getAppointmentStatusMeta(appt.isConfirmed);
    const initials = getPatientInitials(patient);

    const handleCheckbox = (event) => onCheckChange(appt.id, event.target.checked);

    const handleConfirmCancel = async (confirmed) => {
        try {
            setUpdating(true);
            await appointmentService.updateAppointmentStatus(appt.clinicId, appt.id, confirmed);
            showSuccess({
                title: confirmed ? 'Appointment Confirmed' : 'Appointment Cancelled',
                text: confirmed
                    ? 'The appointment was marked as confirmed.'
                    : 'The appointment was marked as cancelled.',
            });
            onStatusUpdated?.();
        } catch (error) {
            console.error(error);
            showError({ title: 'Update Failed', text: 'Failed to update appointment status.' });
        } finally {
            setUpdating(false);
        }
    };

    const handleViewReason = () => {
        showInfo({
            title: 'Appointment Reason',
            text: appt.reason?.trim() || 'No reason provided for this appointment.',
            timer: null,
        });
    };

    return (
        <tr className="appointments-row">
            <td className="appointments-row__checkbox">
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={isSelected}
                    onChange={handleCheckbox}
                    aria-label={`Select appointment for ${patient.firstName || ''} ${patient.lastName || ''}`.trim()}
                />
            </td>

            <td>
                <div className="appointments-patient-cell">
                    <span className="appointments-patient-cell__avatar" aria-hidden="true">
                        {initials || <i className="bi bi-person-fill" />}
                    </span>
                    <div className="min-width-0">
                        <Link to={`/patient-files/${appt.patientId}`} className="appointments-patient-cell__name">
                            {patient.firstName} {patient.lastName}
                        </Link>
                        {patient.preferredName && (
                            <div className="appointments-patient-cell__preferred">{patient.preferredName}</div>
                        )}
                    </div>
                </div>
            </td>

            <td>
                <div className="appointments-time-cell">
                    <span className="appointments-time-cell__range">
                        {appt.startTime} – {appt.endTime}
                    </span>
                </div>
            </td>

            <td>
                <div className="appointments-doctor-cell">
                    <span className="appointments-doctor-cell__name">
                        Dr. {doctor.firstName} {doctor.lastName}
                    </span>
                </div>
            </td>

            <td>
                <div className="appointments-contact-cell">
                    {patient.email ? (
                        <span className="appointments-contact-item">
                            <i className="bi bi-envelope" />
                            {patient.email}
                        </span>
                    ) : null}
                    {patient.cellNumber ? (
                        <span className="appointments-contact-item">
                            <i className="bi bi-phone" />
                            {patient.cellNumber}
                        </span>
                    ) : null}
                    {patient.phoneNumber ? (
                        <span className="appointments-contact-item">
                            <i className="bi bi-telephone" />
                            {patient.phoneNumber}
                        </span>
                    ) : null}
                    {!patient.email && !patient.cellNumber && !patient.phoneNumber ? (
                        <span className="text-muted small">—</span>
                    ) : null}
                </div>
            </td>

            <td>
                <AppointmentReminderPills appointmentStatus={appt.appointmentStatus} />
            </td>

            <td>
                <span className={`appointments-status-pill appointments-status-pill--${statusMeta.variant}`}>
                    <i className={`bi ${statusMeta.icon}`} />
                    {statusMeta.label}
                </span>
            </td>

            <td className="text-end">
                <div className="appointments-actions">
                    {appt.isConfirmed === 0 && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleConfirmCancel(true)}
                            disabled={updating}
                            title="Confirm appointment"
                        >
                            <i className="bi bi-check2-circle" />
                        </button>
                    )}

                    <Dropdown align="end" className="appointments-actions__dropdown">
                        <Dropdown.Toggle
                            variant="light"
                            size="sm"
                            className="appointments-actions__more"
                            disabled={updating}
                            title="More actions"
                        >
                            <i className="bi bi-three-dots" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                            className="shadow-sm"
                            popperConfig={{ strategy: 'fixed' }}
                            renderOnMount
                        >
                            <Dropdown.Item onClick={handleViewReason}>
                                <i className="bi bi-card-text me-2" />
                                View Reason
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                                onClick={() => handleConfirmCancel(true)}
                                disabled={updating}
                            >
                                <i className="bi bi-check2-circle me-2 text-success" />
                                Confirm
                            </Dropdown.Item>
                            <Dropdown.Item
                                className="text-danger"
                                onClick={() => handleConfirmCancel(false)}
                                disabled={updating}
                            >
                                <i className="bi bi-x-circle me-2" />
                                Cancel
                            </Dropdown.Item>
                            <Dropdown.Item as={Link} to={`/patient-files/${appt.patientId}`}>
                                <i className="bi bi-person-vcard me-2" />
                                Open Patient File
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </td>
        </tr>
    );
}

export default AppointmentListItem;
